// mu — claim/release/resolveActorIdentity verbs.
//
// claimTask is the heart of mu's coordination protocol: an atomic
// CAS via a single SQL UPDATE, with two flavours:
//
//   "worker claim"  : --for <name> sets owner_id to that agent (FK to agents.id)
//   "anonymous claim": --self      keeps owner_id=NULL but flips status to
//                                  IN_PROGRESS and records the actor
//                                  in agent_logs
//
// resolveActorIdentity is the env-aware identity helper:
// $MU_AGENT_NAME > pane title > $USER > 'orchestrator'. Used by --self
// AND by the bare worker-claim path, so both flavours answer "who am
// I?" identically.
//
// Extracted from src/tasks.ts as part of refactor_split_large_src_files.

import type { Db } from "../db.js";
import { activeMux } from "../mux.js";
import { withOpContext } from "../op-context.js";
import {
  ClaimerNotRegisteredError,
  TaskAlreadyOwnedError,
  TaskInTriageError,
  TaskNotFoundError,
  TaskParkedError,
} from "./errors.js";
import { type AttributedEvidence, recordEvidenceNote } from "./lifecycle.js";
import { getTask } from "./queries.js";
import { DEFAULT_SUBSTATE, type TaskStatus } from "./status.js";

export interface ReleaseResult {
  /** The previous owner (null if the task was already unowned). */
  previousOwnerName: string | null;
  /** Status before the release. */
  previousStatus: TaskStatus;
  /** Status after the release. */
  status: TaskStatus;
  /** True iff owner OR status actually changed. */
  changed: boolean;
}

export interface ReleaseTaskOptions extends AttributedEvidence {
  /** Workstream context for the task (v5: tasks.local_id is
   *  per-workstream unique). */
  workstream: string;
  /** Force `status = OPEN` regardless of the current status. Without
   *  this flag, `IN_PROGRESS` is also flipped to `OPEN` automatically
   *  (so a released task isn't left structurally stranded with
   *  `owner=NULL, status=IN_PROGRESS`); CLOSED is preserved.
   *  `--reopen` is the override for the rarer "un-close and hand
   *  back to the pool" workflow. */
  reopen?: boolean;
}

/**
 * Release a task: clear `tasks.owner`.
 *
 * Status side-effects (review_release_open_in_progress_inconsistency):
 *   - IN_PROGRESS → OPEN automatically (without it, the task is
 *     stranded: no owner to drive it forward, but `mu task next`
 *     skips it because it's not OPEN).
 *   - OPEN / CLOSED preserved.
 *   - `--reopen` forces OPEN regardless of current status — the
 *     escape hatch for un-closing a CLOSED owned task in one verb.
 *
 * Idempotent: releasing an already-unowned task with no `--reopen` and
 * no IN_PROGRESS status is a no-op (returns `changed: false`).
 * Throws TaskNotFoundError on missing.
 */
export function releaseTask(db: Db, localId: string, opts: ReleaseTaskOptions): ReleaseResult {
  return withOpContext(db, { intent: "task.release", actor: opts.author, group: "new" }, () =>
    releaseTaskImpl(db, localId, opts),
  );
}

function releaseTaskImpl(db: Db, localId: string, opts: ReleaseTaskOptions): ReleaseResult {
  const before = getTask(db, localId, opts.workstream);
  if (!before) throw new TaskNotFoundError(localId);

  // Default: auto-flip IN_PROGRESS → OPEN so the released task isn't
  // left in the structurally weird owner=NULL/IN_PROGRESS limbo.
  // --reopen still wins for any status (including CLOSED).
  const newStatus: TaskStatus = opts.reopen
    ? "OPEN"
    : before.status === "IN_PROGRESS"
      ? "OPEN"
      : before.status;
  const ownerChanges = before.ownerName !== null;
  const statusChanges = newStatus !== before.status;

  if (!ownerChanges && !statusChanges) {
    return {
      previousOwnerName: before.ownerName,
      previousStatus: before.status,
      status: before.status,
      changed: false,
    };
  }

  // No pre-mutation snapshot: v9 dropped the `snapshots` table and
  // rollback is inverse ops over the ops log (`mu undo`).

  db.prepare(
    `UPDATE tasks SET owner_id = NULL, status = ?, substate = ?, updated_at = ?
      WHERE local_id = ?
        AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)`,
  ).run(
    newStatus,
    statusChanges ? DEFAULT_SUBSTATE[newStatus] : before.substate,
    new Date().toISOString(),
    localId,
    before.workstreamName,
  );
  // No emitEvent: the UPDATE fired the capture trigger
  // (intent='task.release'), whose payload names owner_id and status —
  // the same facts the prose spelled out. Evidence goes to a note,
  // since the prose payload that used to carry it is gone.
  recordEvidenceNote(db, localId, before.workstreamName, "RELEASE", opts);
  return {
    previousOwnerName: before.ownerName,
    previousStatus: before.status,
    status: newStatus,
    changed: true,
  };
}

// ─── claimTask (verb) ──────────────────────────────────────────────────

export interface ClaimTaskOptions extends AttributedEvidence {
  /** Workstream context for both the task and the claiming agent.
   *  v5: agents.name and tasks.local_id are per-workstream unique;
   *  the task lookup AND the agent FK lookup scope to this
   *  workstream so a same-named task or worker elsewhere can't be
   *  silently picked. The CLI always passes this from the resolved
   *  -w / $MU_SESSION. */
  workstream: string;
  /**
   * Override the agent name. If omitted, resolved from the ambient
   * environment via `resolveWorkerIdentity()`: `$MU_AGENT_NAME` first,
   * then the current pane's title.
   *
   * Mutually exclusive with `self: true`.
   */
  agentName?: string;
  /**
   * Workstream that the claimer agent lives in. When omitted, defaults
   * to `opts.workstream` (today's same-workstream behaviour). Set by
   * the CLI when `mu task claim X -w A --for B/worker-1` qualifies the
   * `--for` ref with a different workstream prefix
   * (`task_claim_for_cross_workstream`).
   *
   * Cross-workstream ownership is structurally allowed by the schema:
   * `tasks.owner_id` is an INTEGER FK to `agents.id` with no
   * workstream qualifier on the agent side. The per-workstream UNIQUE
   * on `agents(workstream_id, name)` is what previously made the
   * SDK's name → id lookup scope to one workstream; this option
   * widens that lookup to a different workstream when the operator
   * dispatches across a workstream boundary. The agent's own
   * workstream remains unchanged — only the task's `owner_id` points
   * out-of-workstream.
   */
  agentWorkstream?: string;
  /**
   * Anonymous claim: write `owner_id = NULL` instead of resolving an agent
   * name and checking the FK. Use when the actor is the orchestrator
   * (or a script, or a human) doing direct work in a workstream they
   * aren't a registered worker in.
   *
   * The actor name is still recorded — it ends up in `agent_logs.source`
   * for the auto-emitted `task claim` event — so provenance is preserved.
   * Just not in the FK column.
   *
   * Resolution order for the actor name (used as the log source):
   *   1. `actor` if explicitly passed.
   *   2. `$MU_AGENT_NAME`.
   *   3. The mux's current agent name (pane title on tmux).
   *   4. `$USER`.
   *   5. The literal string 'orchestrator'.
   *
   * Mutually exclusive with `agentName` (the two are alternative
   * answers to "who's the actor for this claim?"). Passing both is a
   * usage error.
   */
  self?: boolean;
  /**
   * Override the actor name used for the log source when `self: true`.
   * Ignored when `self: false`. Useful when the orchestrator wants to
   * attribute the work to a meaningful name rather than the pane
   * title (e.g. "deploy-bot" rather than "pi-mu").
   */
  actor?: string;
  /**
   * Claim an OPEN/parked task anyway. Without it, a parked task throws
   * TaskParkedError before any write: parking means "keep out of the
   * scheduler", so overriding it must be explicit.
   */
  force?: boolean;
}

export interface ClaimResult {
  /** The agent now owning the task, or null when the claim was anonymous (--self). */
  ownerName: string | null;
  /** The actor recorded in the agent_logs event — the agent name for a
   *  registered-worker claim, or the resolved actor for --self. */
  actorName: string;
  /** The previous owner (null if it was unowned). */
  previousOwnerName: string | null;
  /** The status BEFORE the claim; post-claim is IN_PROGRESS unless was CLOSED. */
  previousStatus: TaskStatus;
  /** The status AFTER the claim. */
  status: TaskStatus;
}

/**
 * Claim a task. Two modes:
 *
 *   Worker claim (default):
 *     Resolve an agent name from `opts.agentName`, else $MU_AGENT_NAME,
 *     else the mux's current agent name (pane title on tmux). The name
 *     MUST exist in the agents table (`tasks.owner_id` is an FK to
 *     `agents.id`). Sets `owner_id` to that agent. This is what mu-spawned
 *     workers do, and what `mu task claim --for <worker>` does for
 *     orchestrator dispatch.
 *
 *   Anonymous claim (--self):
 *     Skip the name -> agents FK lookup entirely. Sets `owner_id = NULL`.
 *     Records the actor in `agent_logs.source` instead. This is the
 *     orchestrator-doing-direct-work path — the actor is logged but
 *     not registered as a worker pane.
 *
 * Status side-effect: OPEN -> IN_PROGRESS; IN_PROGRESS / CLOSED unchanged.
 *
 * Concurrency: the worker-claim path uses a single-statement CAS UPDATE
 * with `WHERE owner IS NULL OR owner = ?` so two workers racing to
 * claim the same task can't both win. The anonymous path uses
 * `WHERE owner IS NULL` (anonymous claims don't 'own' the task in any
 * exclusive sense; if it's already owned by anyone, the anonymous claim
 * is a TaskAlreadyOwnedError just like a worker claim would be).
 */
export async function claimTask(
  db: Db,
  localId: string,
  opts: ClaimTaskOptions,
): Promise<ClaimResult> {
  // claimTask is async (it shells out to tmux to resolve the caller's
  // identity), and withOpContext is deliberately sync — see its doc
  // comment. So set the context around the SYNCHRONOUS mutation legs
  // inside, not around the whole async function.
  return claimTaskImpl(db, localId, opts);
}

async function claimTaskImpl(
  db: Db,
  localId: string,
  opts: ClaimTaskOptions,
): Promise<ClaimResult> {
  if (opts.self === true && opts.agentName !== undefined) {
    throw new Error("claimTask: --self and --for are mutually exclusive");
  }

  if (opts.self === true) {
    return claimSelf(db, localId, opts);
  }

  // ── Worker claim path (registered agent owns the task) ──
  const agentName = opts.agentName ?? (await resolveWorkerIdentity());
  if (!agentName) {
    throw new Error(
      "claimTask: no agent name (pass opts.agentName, run inside an mu-spawned pane with $MU_AGENT_NAME or $TMUX_PANE set, or pass --self for an anonymous claim)",
    );
  }

  // Resolve the claiming agent to its surrogate id within the agent's
  // workstream — defaults to opts.workstream (today's same-ws path),
  // or opts.agentWorkstream when the CLI dispatched across a
  // workstream boundary via a qualified `--for <ws>/<name>` ref
  // (task_claim_for_cross_workstream).
  //
  // The schema permits cross-workstream owner_id assignment (FK to
  // agents.id only); the per-workstream UNIQUE on agents.name is the
  // only reason this SELECT was scoped narrowly before. Bare-name
  // dispatch keeps that scope to honour today's behaviour; qualified
  // dispatch widens it to the named workstream so the agent resolves
  // there.
  const claimerWorkstream = opts.agentWorkstream ?? opts.workstream;
  const claimerRow = db
    .prepare(
      `SELECT a.id AS id
         FROM agents a JOIN workstreams ws ON ws.id = a.workstream_id
        WHERE a.name = ? AND ws.name = ?`,
    )
    .get(agentName, claimerWorkstream) as { id: number } | undefined;
  if (!claimerRow) {
    const paneIdFromEnv = opts.agentName === undefined ? (process.env.TMUX_PANE ?? null) : null;
    throw new ClaimerNotRegisteredError(agentName, paneIdFromEnv);
  }

  return withOpContext(db, { intent: "task.claim", actor: agentName, group: "new" }, () =>
    db
      .transaction(() => {
        // Resolve the task within opts.workstream. This locks the
        // (workstream, local_id) pair for the rest of the transaction.
        const before = getTask(db, localId, opts.workstream);
        if (!before) throw new TaskNotFoundError(localId);
        assertNotParked(before, opts);

        const now = new Date().toISOString();
        const result = db
          .prepare(
            `UPDATE tasks
            SET owner_id = ?,
                status = CASE WHEN status = 'OPEN' THEN 'IN_PROGRESS' ELSE status END,
                -- SQLite evaluates every SET expression against the OLD
                -- row, so this CASE still sees the pre-claim status.
                substate = CASE WHEN status = 'OPEN' THEN 'active' ELSE substate END,
                updated_at = ?
          WHERE local_id = ?
            AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)
            AND (owner_id IS NULL OR owner_id = ?)`,
          )
          .run(claimerRow.id, now, localId, opts.workstream, claimerRow.id);

        if (result.changes === 0) {
          throw new TaskAlreadyOwnedError(localId, before.ownerName ?? "<unknown>");
        }

        const after = getTask(db, localId, opts.workstream);
        if (!after) throw new Error(`claimTask: row missing after update: ${localId}`);
        // The CLAIM note's author: the dispatcher when the CLI passed one
        // (`--for`), else the claiming agent.
        recordEvidenceNote(db, localId, opts.workstream, "CLAIM", {
          ...opts,
          author: opts.author ?? agentName,
        });
        // No emitEvent: the UPDATE fired the capture trigger under
        // intent='task.claim' with actor=agentName (withOpContext above
        // put it in _op_ctx, and the trigger copies it into ops.actor).
        // The op payload carries the new owner_id, so the prose
        // `formatClaimEvent` breadcrumb — and the tab-delimited prefix
        // that existed only because prose had to be re-parsed — are both
        // redundant. `lastClaimActor` now reads ops.actor directly.
        return {
          ownerName: agentName,
          actorName: agentName,
          previousOwnerName: before.ownerName,
          previousStatus: before.status,
          status: after.status,
        };
      })
      .immediate(),
  );
}

/** The parked-claim guard shared by the worker and --self paths. The
 *  UPDATE's `status = 'OPEN'` CASE already maps parked → active, so a
 *  forced claim needs nothing else. */
function assertNotParked(
  task: { name: string; status: TaskStatus; substate: string; workstreamName: string },
  opts: ClaimTaskOptions,
): void {
  if (task.status === "OPEN" && task.substate === "parked" && opts.force !== true) {
    throw new TaskParkedError(task.name, task.workstreamName);
  }
  if (task.status === "OPEN" && task.substate === "triage" && opts.force !== true) {
    throw new TaskInTriageError(task.name, task.workstreamName);
  }
}

/**
 * Resolve the current actor's identity for attribution in task notes,
 * --self claims, and any other write that wants 'who did this?'.
 *
 * Resolution order:
 *   1. $MU_AGENT_NAME env var (set by mu spawnAgent on every managed
 *      pane; surfaced from the f3d4bdd commit). Authoritative when
 *      present — you're inside a mu-spawned worker, no ambiguity.
 *   2. tmux pane title (the pane-title identity step). Works
 *      when running inside any pane mu manages OR adopted.
 *   3. $USER (when running outside tmux entirely).
 *   4. The literal 'orchestrator' as a last-resort default.
 *
 * Why prefer env over pane title: pane titles are a tmux-server-wide
 * resource that anything can rewrite. The env var is set per-pane at
 * spawn time and is unforgeable from outside without explicit
 * `--actor` override. Pane title is the only identity available for
 * adopted panes that didn't go through mu's spawn path.
 */
export async function resolveActorIdentity(): Promise<string> {
  const worker = await resolveWorkerIdentity();
  if (worker !== undefined) return worker;
  const user = process.env.USER;
  if (user !== undefined && user !== "") return user;
  return "orchestrator";
}

/**
 * The AGENT-identity half of the ladder, shared by `resolveActorIdentity`
 * and the bare worker-claim path. Returns undefined when the caller
 * isn't identifiable as a specific agent — the two callers disagree
 * about what to do then, which is why this stops short rather than
 * falling through to `$USER`:
 *
 *   - `resolveActorIdentity` continues to `$USER` / 'orchestrator',
 *     because an anonymous claim only needs an attribution string.
 *   - the worker-claim path THROWS, because `tasks.owner_id` is a real
 *     FK to `agents.id` and '$USER' is not an agent. Falling back there
 *     would turn a clear "who are you?" error into a confusing
 *     ClaimerNotRegisteredError naming the operator's unix login.
 *
 * Rungs:
 *   1. `$MU_AGENT_NAME` — injected into every pane by spawnAgent.
 *      Backend-independent, and the only rung that works on a mux with
 *      no mu-writable pane title.
 *   2. The mux backend's `currentAgentName()` — pane title on tmux.
 *      Parses 'name · status · task' back to the name token, which
 *      matters because composeAgentTitle decorates titles and the FK is
 *      keyed on the bare `agents.name`.
 *
 * Why env before title: a pane title is a mux-server-wide resource that
 * any process can rewrite, while the env var is set once at spawn and
 * is unforgeable from outside the pane. The title rung survives only
 * because ADOPTED panes never went through spawn and have nothing else.
 */
export async function resolveWorkerIdentity(): Promise<string | undefined> {
  const muAgent = process.env.MU_AGENT_NAME;
  if (muAgent !== undefined && muAgent !== "") return muAgent;
  // Best-effort: identity resolution must never be the thing that fails
  // a verb. A non-interactive command on a box with no multiplexer wants an
  // attribution string, not a NoMultiplexerError — and the caller
  // already has $USER / 'orchestrator' rungs below this one.
  try {
    const paneTitle = await (await activeMux()).currentAgentName();
    if (paneTitle !== undefined && paneTitle !== "") return paneTitle;
  } catch {
    // No reachable mux, or it could not answer. Fall through.
  }
  return undefined;
}

async function claimSelf(db: Db, localId: string, opts: ClaimTaskOptions): Promise<ClaimResult> {
  const actor =
    opts.actor !== undefined && opts.actor !== "" ? opts.actor : await resolveActorIdentity();
  return withOpContext(db, { intent: "task.claim", actor, group: "new" }, () =>
    db
      .transaction(() => {
        // Scope by the operator's workstream so a same-named task
        // elsewhere can't be self-claimed by accident.
        const before = getTask(db, localId, opts.workstream);
        if (!before) throw new TaskNotFoundError(localId);
        assertNotParked(before, opts);

        // Anonymous claim: owner stays NULL, status flips OPEN -> IN_PROGRESS.
        // Gate on `owner_id IS NULL` so an in-flight worker claim can't be
        // silently overwritten.
        const now = new Date().toISOString();
        const result = db
          .prepare(
            `UPDATE tasks
            SET status = CASE WHEN status = 'OPEN' THEN 'IN_PROGRESS' ELSE status END,
                substate = CASE WHEN status = 'OPEN' THEN 'active' ELSE substate END,
                updated_at = ?
          WHERE local_id = ?
            AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)
            AND owner_id IS NULL`,
          )
          .run(now, localId, before.workstreamName);

        if (result.changes === 0) {
          // Task exists but is already owned (by someone). Mirror the
          // worker-path error so callers can pattern-match consistently.
          throw new TaskAlreadyOwnedError(localId, before.ownerName ?? "<unknown>");
        }

        const after = getTask(db, localId, before.workstreamName);
        if (!after) throw new Error(`claimTask: row missing after update: ${localId}`);
        recordEvidenceNote(db, localId, before.workstreamName, "CLAIM", {
          ...opts,
          author: opts.author ?? actor,
        });
        // No emitEvent. This is the interesting case: the `--self` path
        // leaves tasks.owner_id NULL deliberately, so the op PAYLOAD
        // cannot name the actor — but ops.actor can and does, because
        // withOpContext seeded _op_ctx with it. That is precisely what
        // `lastClaimActor` needs, and reading a column beats
        // prefix-matching prose (review_code_last_claim_actor_brittle).
        return {
          ownerName: null,
          actorName: actor,
          previousOwnerName: before.ownerName,
          previousStatus: before.status,
          status: after.status,
        };
      })
      .immediate(),
  );
}
